import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, type FundoCartao } from "../api";
import {
  type Colaborador,
  abrirJanela,
  baixarDataUrl,
  compactarImagem,
  compartilharArte,
  copiar,
  copiarImagem,
  dataUrlParaArquivo,
  gerarCartaoAniversario,
  guardarUsarWhatsAppWeb,
  iniciais,
  lerUsarWhatsAppWeb,
  linkWhatsApp,
  normalizarNumero,
  podeCompartilharArquivo,
  primeiroNome,
} from "../endomarketing-utils";
import {
  IconClose,
  IconDownload,
  IconCopy,
  IconCheck,
  IconUpload,
  IconChat,
  IconUsers,
} from "../icons";
import { usePodeExcluir } from "../permissoes";

/**
 * Peças compartilhadas pelas telas do Endomarketing: o avatar (foto ou
 * iniciais), a etiqueta de "já parabenizado" e o cartão de aniversário.
 * Ficam separadas para as quatro telas usarem sem importar umas às outras.
 */

export function Avatar({
  pessoa,
  tamanho = "m",
  onClick,
  titulo,
}: {
  pessoa: { nome: string; foto?: string | null };
  tamanho?: "s" | "m" | "g";
  onClick?: () => void;
  titulo?: string;
}) {
  const conteudo = pessoa.foto ? (
    <img src={pessoa.foto} alt={pessoa.nome} />
  ) : (
    <span>{iniciais(pessoa.nome)}</span>
  );
  const cls = `endo-avatar endo-avatar-${tamanho}`;
  return onClick ? (
    <button type="button" className={cls} onClick={onClick} title={titulo}>
      {conteudo}
    </button>
  ) : (
    <div className={cls} title={titulo}>
      {conteudo}
    </div>
  );
}

/** Selo de "já foi parabenizado neste ano" (clicável para desfazer). */
export function SeloParabenizado({ onDesfazer }: { onDesfazer?: () => void }) {
  return (
    <button
      type="button"
      className="endo-selo"
      title={onDesfazer ? "Já parabenizado — clique para desmarcar" : "Já parabenizado"}
      onClick={onDesfazer}
      disabled={!onDesfazer}
    >
      <IconCheck size={13} /> parabenizado
    </button>
  );
}

/* ------------------------------------------------------------------ *
 *  POST DE ANIVERSÁRIO                                                 *
 * ------------------------------------------------------------------ */

/**
 * Último fundo escolhido, lembrado neste navegador: quem monta os posts do mês
 * usa o mesmo fundo para todos, e escolher de novo a cada pessoa cansa.
 * Sempre com try/catch — em aba anônima (e com o armazenamento bloqueado) o
 * localStorage lança, e isso não pode derrubar o cartão.
 */
const CHAVE_FUNDO = "sercamp-rh-endo-fundo-cartao";

function lerFundoPreferido(): string | null {
  try {
    return localStorage.getItem(CHAVE_FUNDO) || null;
  } catch {
    return null;
  }
}

function guardarFundoPreferido(arquivo: string | null) {
  try {
    if (arquivo) localStorage.setItem(CHAVE_FUNDO, arquivo);
    else localStorage.removeItem(CHAVE_FUNDO);
  } catch {
    /* aba anônima / armazenamento bloqueado: só não lembra */
  }
}

/**
 * Pergunta UMA vez se o aparelho manda imagem pelo compartilhamento do sistema,
 * com um arquivo de teste — só para o texto da tela não "piscar" enquanto o
 * post é gerado. A decisão de verdade, no envio, é com o arquivo real.
 */
function aparelhoMandaImagem(): boolean {
  try {
    const teste = new File([new Blob(["."], { type: "image/png" })], "teste.png", { type: "image/png" });
    return podeCompartilharArquivo(teste);
  } catch {
    return false;
  }
}

/** "Maria da Silva" → "maria-da-silva" (nome do arquivo baixado). */
function paraNomeDeArquivo(nome: string): string {
  return (
    String(nome ?? "")
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "aniversariante"
  );
}

type ModeloPost = "sistema" | "pronta";

/**
 * POST DE ANIVERSÁRIO — a imagem que o RH manda para a pessoa ou posta no
 * grupo. Dois modelos:
 *   • CARTÃO DO SISTEMA: gerado no próprio navegador (canvas) com a foto, o
 *     nome e a marca, sobre o verde da SERCAMP ou sobre um FUNDO salvo no
 *     servidor (o modelo do mês feito no Canva, que todo o RH reaproveita).
 *     Dá para trocar a foto só neste post, sem mexer no cadastro;
 *   • IMAGEM PRONTA: a arte feita fora (Canva, designer) vai do jeito que veio.
 *
 * E ENVIA pelo WhatsApp. O link do WhatsApp (wa.me) só leva texto, então:
 *   • aparelho que compartilha arquivo (celular, em geral) → a bandeja do
 *     sistema abre com a imagem e o texto de legenda; a pessoa escolhe a
 *     conversa ou o grupo ali;
 *   • computador → a imagem vai para a área de transferência e a conversa abre
 *     com o texto pronto: é colar (Ctrl+V) e mandar. Vale também para quem
 *     ligou "Usar o WhatsApp Web neste aparelho" (ver lerUsarWhatsAppWeb).
 * Mandar PARA A PESSOA marca como parabenizado (`onEnviado`); mandar para um
 * grupo não, porque não dá para saber se ela está nele.
 */
export function CartaoAniversarioModal({
  pessoa,
  legenda,
  titulo,
  mensagem,
  onEnviado,
  onClose,
}: {
  pessoa: Colaborador;
  legenda: string;
  titulo?: string;
  mensagem: string;
  /** Depois de enviar PARA A PESSOA: quem abriu o post marca o parabéns (do tipo certo). */
  onEnviado?: () => void | Promise<void>;
  onClose: () => void;
}) {
  const podeExcluir = usePodeExcluir(); // excluir fundo é só de administrador
  const nome1 = primeiroNome(pessoa.nome);
  const numero = normalizarNumero(pessoa.telefone);

  const [modelo, setModelo] = useState<ModeloPost>("sistema");
  const [fundos, setFundos] = useState<FundoCartao[]>([]);
  const [fundosFalharam, setFundosFalharam] = useState(false);
  /** Fundo escolhido (nome do arquivo no servidor); null = verde SERCAMP. */
  const [fundo, setFundo] = useState<string | null>(lerFundoPreferido);
  /** Foto usada SÓ neste post (dataURL); null = a do cadastro. */
  const [fotoPost, setFotoPost] = useState<string | null>(null);
  /** A arte pronta que a pessoa subiu (dataURL já compactada). */
  const [imagemPronta, setImagemPronta] = useState<string | null>(null);
  const [cartao, setCartao] = useState<string | null>(null);
  const [gerando, setGerando] = useState(false);
  /** Texto do que está em andamento ("Enviando o fundo…"); null = livre. */
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);
  /** O post como ARQUIVO — pronto antes do clique (ver compartilharArte). */
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [mandaImagem] = useState(aparelhoMandaImagem);
  /** Ignorar a bandeja do sistema e ir pelo WhatsApp Web (preferência do aparelho). */
  const [usarWeb, setUsarWeb] = useState(lerUsarWhatsAppWeb);
  const fundoRef = useRef<HTMLInputElement>(null);
  const fotoRef = useRef<HTMLInputElement>(null);
  const prontaRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Fundos salvos no servidor (a lista é a mesma para todo o RH).
  useEffect(() => {
    let vivo = true;
    api.endomarketing.fundos
      .listar()
      .then((lista) => {
        if (!vivo) return;
        const ok = lista.filter((f) => f.disponivel !== false);
        setFundos(ok);
        // O fundo lembrado foi excluído nesse meio-tempo: volta para o verde.
        setFundo((atual) => (atual && !ok.some((f) => f.arquivo === atual) ? null : atual));
      })
      .catch(() => vivo && setFundosFalharam(true));
    return () => {
      vivo = false;
    };
  }, []);

  const fotoUsada = fotoPost ?? pessoa.foto;
  const fundoUrl = fundo ? api.endomarketing.fundos.url(fundo) : null;

  // Gera (de novo) o cartão a cada troca de fundo ou de foto.
  useEffect(() => {
    if (modelo !== "sistema") {
      // Trocou de modelo no meio da geração: o "Gerando…" não pode ficar preso.
      setGerando(false);
      return;
    }
    let vivo = true;
    setGerando(true);
    gerarCartaoAniversario({ nome: pessoa.nome, foto: fotoUsada, legenda, titulo, fundo: fundoUrl })
      .then((url) => vivo && setCartao(url))
      .catch((e) => vivo && setErro(e instanceof Error ? e.message : "Erro ao gerar o cartão."))
      .finally(() => vivo && setGerando(false));
    return () => {
      vivo = false;
    };
  }, [modelo, pessoa.nome, fotoUsada, legenda, titulo, fundoUrl]);

  const imagem = modelo === "pronta" ? imagemPronta : cartao;
  const nomeArquivo = `post-${paraNomeDeArquivo(pessoa.nome)}.${
    imagem?.startsWith("data:image/jpeg") ? "jpg" : "png"
  }`;

  // O arquivo do post fica pronto ANTES do clique: o compartilhamento do
  // sistema só abre enquanto o clique está "fresco", e converter a imagem na
  // hora gastaria esse tempo no celular.
  useEffect(() => {
    let vivo = true;
    setArquivo(null);
    if (imagem) {
      dataUrlParaArquivo(imagem, nomeArquivo)
        .then((f) => vivo && setArquivo(f))
        .catch(() => {});
    }
    return () => {
      vivo = false;
    };
  }, [imagem, nomeArquivo]);

  const compartilhaAqui = useMemo(
    () => !usarWeb && (arquivo ? podeCompartilharArquivo(arquivo) : mandaImagem),
    [arquivo, mandaImagem, usarWeb],
  );

  function escolherFundo(arquivoFundo: string | null) {
    setFundo(arquivoFundo);
    guardarFundoPreferido(arquivoFundo);
  }

  /** Sobe um fundo novo: fica salvo no servidor para todo o RH. */
  async function enviarFundo(file: File | null) {
    if (!file) return;
    setErro(null);
    setAviso(null);
    setOcupado("Enviando o fundo…");
    try {
      // 1920px no lado maior (e não 1080): num fundo em pé, tipo story, o lado
      // menor ainda cobre o quadrado de 1080 sem esticar a imagem.
      const dataUrl = await compactarImagem(file, 1920, 0.88);
      const base = file.name.replace(/\.[^.]*$/, "").slice(0, 80) || "fundo";
      const r = await api.endomarketing.fundos.enviar(await dataUrlParaArquivo(dataUrl, `${base}.jpg`));
      const lista = await api.endomarketing.fundos.listar().catch(() => null);
      if (lista) setFundos(lista.filter((f) => f.disponivel !== false));
      escolherFundo(r.arquivo);
      setAviso("Fundo salvo — ele fica disponível para todo o RH usar nos posts.");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível enviar o fundo.");
    } finally {
      setOcupado(null);
      if (fundoRef.current) fundoRef.current.value = "";
    }
  }

  async function excluirFundo(f: FundoCartao) {
    if (!confirm("Excluir este fundo? Ele deixa de aparecer para todo o RH.")) return;
    setErro(null);
    try {
      await api.endomarketing.fundos.remover(f.arquivo);
      setFundos((lista) => lista.filter((x) => x.arquivo !== f.arquivo));
      if (fundo === f.arquivo) escolherFundo(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível excluir o fundo.");
    }
  }

  /** Outra foto SÓ neste post — o cadastro da pessoa não muda. */
  async function trocarFoto(file: File | null) {
    if (!file) return;
    setErro(null);
    try {
      setFotoPost(await compactarImagem(file, 800, 0.9));
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível usar esta foto.");
    } finally {
      if (fotoRef.current) fotoRef.current.value = "";
    }
  }

  /** Arte feita fora: vai do jeito que veio, só compactada para o WhatsApp. */
  async function usarImagemPronta(file: File | null) {
    if (!file) return;
    setErro(null);
    try {
      setImagemPronta(await compactarImagem(file, 1080, 0.9));
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível usar esta imagem.");
    } finally {
      if (prontaRef.current) prontaRef.current.value = "";
    }
  }

  /**
   * Manda o post. `pessoa` abre a conversa dela pelo número; `grupo` abre o
   * WhatsApp para escolher a conversa (o wa.me não aceita endereço de grupo).
   */
  async function enviar(destino: "pessoa" | "grupo") {
    if (!imagem || !arquivo) return;
    if (destino === "pessoa" && !numero) return;
    setErro(null);
    setAviso(null);

    // O parabéns é marcado só depois de o envio sair; falha em marcar não
    // desfaz o que já foi para o WhatsApp.
    const marcar = async () => {
      if (destino !== "pessoa" || !onEnviado) return;
      try {
        await onEnviado();
      } catch {
        /* a marca fica para o botão "Parabenizar" */
      }
    };

    if (compartilhaAqui) {
      try {
        const foi = await compartilharArte(arquivo, mensagem);
        if (!foi) {
          // Fechou a bandeja: não conta como enviado. No computador, isso
          // costuma ser o WhatsApp que não aparece na lista do Windows.
          setAviso(
            "Envio cancelado. Se o WhatsApp não apareceu na lista, marque “Usar o WhatsApp Web neste aparelho” e envie de novo.",
          );
          return;
        }
        await marcar();
        setAviso(
          destino === "pessoa"
            ? `Pronto — ${nome1} ficou marcado(a) como parabenizado(a).`
            : "Pronto — o post foi compartilhado.",
        );
        return;
      } catch {
        // A bandeja falhou: segue pelo caminho do computador em vez de travar.
      }
    }

    // Copia ANTES de abrir a aba: com a aba nova na frente, a página perde o
    // foco e o navegador recusa escrever na área de transferência.
    const copiou = await copiarImagem(imagem);
    if (!abrirJanela(linkWhatsApp(destino === "pessoa" ? numero : "", mensagem))) {
      setErro("O navegador bloqueou a nova aba. Libere os pop-ups deste site e tente de novo.");
      return;
    }
    await marcar();
    setAviso(
      copiou
        ? destino === "pessoa"
          ? `Imagem copiada. Na conversa de ${nome1} que abriu, cole com Ctrl+V e envie — o texto já está na caixa.`
          : "Imagem copiada. No WhatsApp que abriu, escolha o grupo, cole com Ctrl+V e envie — o texto já está na caixa."
        : "Não consegui copiar a imagem neste navegador. Clique em “Baixar imagem” e anexe na conversa — o texto já vai pronto.",
    );
  }

  const podeEnviar = !!imagem && !!arquivo && !ocupado;

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card endo-post-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Post de {nome1}</h3>
          <button type="button" className="icon-btn" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body endo-post">
          <div className="endo-post-controles">
            <div className="endo-post-grupo">
              <span className="endo-post-rotulo">Modelo do post</span>
              <div className="endo-chips">
                <button
                  type="button"
                  className={`endo-chip ${modelo === "sistema" ? "ativo" : ""}`}
                  onClick={() => setModelo("sistema")}
                >
                  Cartão do sistema
                </button>
                <button
                  type="button"
                  className={`endo-chip ${modelo === "pronta" ? "ativo" : ""}`}
                  onClick={() => setModelo("pronta")}
                >
                  Imagem pronta
                </button>
              </div>
            </div>

            {modelo === "sistema" ? (
              <>
                <div className="endo-post-grupo">
                  <span className="endo-post-rotulo">Fundo</span>
                  <div className="endo-fundos">
                    <button
                      type="button"
                      className={`endo-fundo endo-fundo-marca ${!fundo ? "ativo" : ""}`}
                      title="Verde SERCAMP"
                      aria-pressed={!fundo}
                      onClick={() => escolherFundo(null)}
                    >
                      Verde SERCAMP
                    </button>
                    {fundos.map((f) => (
                      <div key={f.arquivo} className="endo-fundo-item">
                        <button
                          type="button"
                          className={`endo-fundo ${fundo === f.arquivo ? "ativo" : ""}`}
                          title={f.nome ? `Fundo: ${f.nome}` : "Fundo salvo"}
                          aria-label={f.nome ? `Usar o fundo ${f.nome}` : "Usar este fundo"}
                          aria-pressed={fundo === f.arquivo}
                          onClick={() => escolherFundo(f.arquivo)}
                        >
                          <img src={api.endomarketing.fundos.url(f.arquivo)} alt="" loading="lazy" />
                        </button>
                        {podeExcluir && (
                          <button
                            type="button"
                            className="endo-fundo-tirar"
                            title="Excluir este fundo"
                            aria-label="Excluir este fundo"
                            onClick={() => excluirFundo(f)}
                          >
                            <IconClose size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="endo-fundo endo-fundo-novo"
                      onClick={() => fundoRef.current?.click()}
                      disabled={!!ocupado}
                      title="Subir uma imagem de fundo (fica salva para todo o RH)"
                    >
                      <IconUpload size={16} />
                      Enviar fundo
                    </button>
                    <input
                      ref={fundoRef}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => enviarFundo(e.target.files?.[0] ?? null)}
                    />
                  </div>
                  <small className="muted">
                    {fundosFalharam
                      ? "Não consegui carregar os fundos salvos — o verde continua disponível."
                      : "O fundo enviado fica salvo para todo o RH (ex.: o modelo do mês feito no Canva)."}
                  </small>
                </div>

                <div className="endo-post-grupo">
                  <span className="endo-post-rotulo">Foto</span>
                  <div className="endo-arte-botoes">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => fotoRef.current?.click()}
                    >
                      <IconUpload size={15} /> Trocar a foto (só neste post)
                    </button>
                    {fotoPost && (
                      <button type="button" className="btn-secondary btn-sm" onClick={() => setFotoPost(null)}>
                        Voltar à foto do cadastro
                      </button>
                    )}
                    <input
                      ref={fotoRef}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => trocarFoto(e.target.files?.[0] ?? null)}
                    />
                  </div>
                  <small className="muted">
                    {fotoPost
                      ? "Usando outra foto só neste post — o cadastro não muda."
                      : pessoa.foto
                        ? "Usando a foto do cadastro."
                        : "Sem foto no cadastro: o cartão sai com as iniciais."}
                  </small>
                </div>
              </>
            ) : (
              <div className="endo-post-grupo">
                <span className="endo-post-rotulo">Imagem pronta</span>
                <div className="endo-arte-botoes">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => prontaRef.current?.click()}
                  >
                    <IconUpload size={15} /> {imagemPronta ? "Escolher outra imagem" : "Escolher a imagem"}
                  </button>
                  <input
                    ref={prontaRef}
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => usarImagemPronta(e.target.files?.[0] ?? null)}
                  />
                </div>
                <small className="muted">
                  A arte feita fora (Canva, designer) vai do jeito que veio — só é compactada para o
                  WhatsApp. Ela não fica salva no sistema.
                </small>
              </div>
            )}

            {ocupado && <p className="endo-post-ocupado">{ocupado}</p>}
            {erro && <div className="form-error">{erro}</div>}
            {aviso && (
              <div className="endo-aviso endo-post-aviso">
                <span>{aviso}</span>
              </div>
            )}

            {/* Honesta sobre o que ESTE aparelho consegue: o link do WhatsApp
                não leva imagem, e o caminho muda no celular e no computador. */}
            <p className="endo-arte-dica">
              {compartilhaAqui ? (
                <>
                  <IconCheck size={14} /> Neste aparelho a imagem vai <strong>junto</strong>, com o
                  texto de legenda: ao enviar, escolha a conversa ou o grupo na bandeja do WhatsApp.
                </>
              ) : (
                <>
                  Neste computador o link do WhatsApp só leva texto: ao enviar, a imagem é{" "}
                  <strong>copiada</strong> e a conversa abre com o texto pronto — é colar com{" "}
                  <strong>Ctrl+V</strong> e mandar. Pelo <strong>celular</strong>, a imagem vai junto.
                </>
              )}
            </p>
            {mandaImagem && (
              <label className="endo-web">
                <input
                  type="checkbox"
                  checked={usarWeb}
                  onChange={(e) => {
                    setUsarWeb(e.target.checked);
                    guardarUsarWhatsAppWeb(e.target.checked);
                  }}
                />
                <span>
                  Usar o WhatsApp Web neste aparelho{" "}
                  <small className="muted">(quando o WhatsApp não aparece na bandeja do sistema)</small>
                </span>
              </label>
            )}
            {!numero && (
              <p className="endo-alerta endo-post-sem-telefone">
                {pessoa.nome} está sem telefone no cadastro: “Enviar para {nome1}” fica desligado. Dá
                para mandar num grupo ou baixar a imagem.
              </p>
            )}
          </div>

          <div className="endo-post-previa">
            {imagem ? (
              <img className={gerando ? "gerando" : ""} src={imagem} alt={`Post de ${pessoa.nome}`} />
            ) : modelo === "pronta" ? (
              <div className="endo-arte-vazia">
                Nenhuma imagem escolhida.
                <small>Clique em “Escolher a imagem” e suba a arte pronta.</small>
              </div>
            ) : (
              <div className="endo-arte-vazia">{erro ? "Não foi possível gerar o cartão." : "Gerando o cartão…"}</div>
            )}
            {gerando && imagem && <span className="endo-post-gerando">Gerando…</span>}
          </div>
        </div>

        <div className="modal-foot endo-post-acoes">
          <button
            type="button"
            className="btn-secondary"
            onClick={async () => {
              setCopiado(await copiar(mensagem));
              setTimeout(() => setCopiado(false), 2000);
            }}
          >
            <IconCopy size={16} /> {copiado ? "Texto copiado!" : "Copiar texto"}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={!imagem}
            onClick={() => imagem && baixarDataUrl(imagem, nomeArquivo)}
          >
            <IconDownload size={16} /> Baixar imagem
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={!podeEnviar}
            onClick={() => enviar("grupo")}
            title="Abre o WhatsApp para você escolher o grupo"
          >
            <IconUsers size={16} /> Enviar para um grupo
          </button>
          <button
            type="button"
            className="btn-whats endo-post-enviar"
            disabled={!podeEnviar || !numero}
            onClick={() => enviar("pessoa")}
            title={
              numero
                ? `Abre a conversa de ${nome1} com o post`
                : `${pessoa.nome} está sem telefone no cadastro`
            }
          >
            <IconChat size={16} /> Enviar para {nome1}
          </button>
        </div>
      </div>
    </div>
  );
}
